import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import "../modules.css";
import { globalVariables } from "helper/globalVariables";
import { TextInput } from "shared/Components/textInput";
import { avatar } from "helper/images";
import { Button } from "shared/Components/button";
import CustomDatePicker from "shared/Components/dateOfBirthPicker";
import { FileUpload } from "shared/Components/fileUpload";
import { OutlineButton } from "shared/Components/outlineButton";
import { trialDetailsResolver } from "validators/UserValidation";
import { StepsHeader } from "shared/Components/stepsHeader";
import { SectionHeading } from "../common/SectionHeading";
import { useDispatch, useSelector } from "react-redux";
import { setTrialDetails } from "redux/features/hostSlice/addTrialSlice/addTrialSlice";
import { SearchLocation } from "shared/Components/searchLocation";
import { MultiSelectChipsWithSearchDropdown } from "shared/Components/multiSelectWithSearchAndChips";
import { client } from "helper/apolloConfig";
import { getAllJudgeQuery } from "redux/features/hostSlice/addTrialSlice/addTrialGql";
import { AutoCompleteInput } from "shared/Components/autoComplete";

export const TrialDetails = (props) => {
  const { onTapPrevious, onTapNext, saveTrialDetails } = props || {};

  const TIME_ZONES = [
    "EasternTime",
    "CentralTime",
    "MountainTime",
    "PacificTime",
    "AlaskaTime",
    "Hawaii-AleutianTime",
  ];

  const { auth } = useSelector((state) => state);
  const dispatch = useDispatch();
  const [checkValidation, setCheckValidation] = useState(false);
  const [judgeError, setJudgeError] = useState(false);
  const [formData, setFormData] = useState({
    city: "",
    state: "",
    timeZone: "",
    trialDate: "",
    openDate: "",
    closeDate: "",
    cutoffDate: "",
    image: "",
    fileName: "",
    fileSize: "",
    uploadImageFileName: "",
    uploadImageFileSize: "",
    addedJudges: [],
  });
  const [fileLoading, setFileLoading] = useState(false);
  const [uploadImageLoading, setUploadImageLoading] = useState(false);
  const [numberJudges, setNumberJudges] = useState(0);
  const [judgesDropdownData, setJudgesDropdownData] = useState([]);

  useEffect(() => {
    if (saveTrialDetails) {
      getTrialDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saveTrialDetails]);

  useEffect(() => {
    getJudges();
  }, []);

  const getTrialDetails = async () => {
    if (auth?.addTrialReducer?.trialDetails) {
      const trialDetails = auth?.addTrialReducer?.trialDetails;
      setValue("trialName", trialDetails?.trialName);
      setValue("trialLocationName", trialDetails?.trialLocationName);
      setValue("trialLocationAddress", trialDetails?.trialLocationAddress);
      setValue("zip", trialDetails?.zip);
      setValue("city", trialDetails?.city);
      setValue("state", trialDetails?.state);
      setValue("description", trialDetails?.description);
      setValue("notes", trialDetails?.notes);
      setValue("maxEntries", trialDetails?.maxEntries);
      setValue("numberOfJudges", trialDetails?.numberOfJudges);
      setValue("timeZone", trialDetails?.timeZone);
      setValue("openDate", trialDetails?.openDate);
      setValue("closeDate", trialDetails?.closeDate);
      setValue("cutoffDate", trialDetails?.cutoffDate);
      setValue("trialDate", trialDetails?.trialDate);
      setFormData({
        ...formData,
        city: trialDetails?.city,
        state: trialDetails?.state,
        timeZone: trialDetails?.timeZone,
        trialDate: trialDetails?.trialDate,
        openDate: trialDetails?.openDate,
        closeDate: trialDetails?.closeDate,
        cutoffDate: trialDetails?.cutoffDate,
        image: trialDetails?.image,
        fileName: trialDetails?.fileName,
        fileSize: trialDetails?.fileSize,
        addedJudges: trialDetails?.addedJudges,
      });
      setNumberJudges(trialDetails?.numberOfJudges);
    }
  };

  const getJudges = async () => {
    try {
      const result = await client.query({
        query: getAllJudgeQuery,
        variables: {
          search: "",
        },
        fetchPolicy: 'network-only', // Always fetch fresh data
      });

      let data = result?.data?.getAllJudge;
      if (data) {
        // Sort judges alphabetically by name
        const sortedData = [...data].sort((a, b) => {
          const nameA = a?.name?.toLowerCase() || '';
          const nameB = b?.name?.toLowerCase() || '';
          return nameA.localeCompare(nameB);
        });
        setJudgesDropdownData(sortedData);
      }
    } catch (err) {}
  };

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    getValues,
  } = useForm({
    resolver: trialDetailsResolver,
    defaultValues: {
      trialName: "",
      trialLocationName: "",
      trialLocationAddress: "",
      zip: "",
      description: "",
      notes: "",
    },
  });

  const onCheckValidation = () => {
    setCheckValidation(true);
  };

  const onHandleSubmit = (credentials) => {
    if (
      formData.city &&
      formData.state &&
      formData.timeZone &&
      formData.trialDate &&
      formData.openDate &&
      formData.closeDate &&
      formData.cutoffDate &&
      !judgeError
    ) {
      dispatch(setTrialDetails({ ...credentials, ...formData }));
      onTapNext?.();
    }
  };

  const handleFormData = (key, value) => {
    setFormData({ ...formData, [key]: value });
  };

  const tapPrevious = () => {
    dispatch(setTrialDetails({ ...getValues(), ...formData }));
    onTapPrevious?.();
  };

  const onAddJudges = (judges) => {
    setFormData({ ...formData, addedJudges: judges });
  };

  const innerContainerClass = "block lg:flex flex-row w-full lg:w-auto";

  useEffect(() => {
    const selectedCount = formData.addedJudges?.length || 0;
    if (numberJudges > 0) {
      if (selectedCount == numberJudges) {
        setJudgeError(false);
      } else if (numberJudges > selectedCount) {
        setJudgeError(true);
      } else {
        setJudgeError(true);
      }
    } else {
      setJudgeError(false);
    }
  }, [numberJudges, formData.addedJudges]);

  return (
    <div className="flex justify-center flex-col items-center">
      <StepsHeader
        step={2}
        labels={[
          globalVariables.hostDetails,
          globalVariables.trialDetails,
          globalVariables.searchDetails,
        ]}
      />
      <SectionHeading heading={globalVariables.trialDetails} />
      <form
        className="lg:w-full items-center flex flex-col max-[400px]:w-[90%]"
        onSubmit={handleSubmit(onHandleSubmit)}
      >
        <div className="max-[400px]:w-[100%] pb-[20px]">
          <div className={innerContainerClass}>
            <TextInput
              register={register}
              name="trialName"
              placeholder={globalVariables.trialName}
              label={globalVariables.trialName}
              errorMsg={errors.trialName?.message}
              onKeyPress={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                }
              }}
            />
            <div className="mt-[10px] lg:mt-0 mr-0 lg:mr-5" />
            <TextInput
              register={register}
              name="trialLocationName"
              placeholder={globalVariables.trialLocationName}
              label={globalVariables.trialLocationName}
              errorMsg={errors.trialLocationName?.message}
              onKeyPress={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                }
              }}
            />
          </div>
          <div className="mt-[10px] lg:mt-[10px]" />
          <div className={innerContainerClass}>
            <div>
              <TextInput
                register={register}
                name="trialLocationAddress"
                placeholder={globalVariables.trialLocationAddress}
                label={globalVariables.trialLocationAddress}
                errorMsg={errors.trialLocationAddress?.message}
                onKeyPress={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                  }
                }}
              />
            </div>
            <div className="mt-[10px] lg:mt-0 mr-0 lg:mr-5" />
              <SearchLocation
                register={register}
                icon="right"
                name="state"
                defaultValue={formData.state}
                placeholder={globalVariables.state}
                label={globalVariables.state}
                types={["(regions)"]}
                onSelectLocation={(location) => {
                  handleFormData("state", location);
                }}
                errorMsg={
                  !formData.state && checkValidation
                    ? "Please select a state from the dropdown"
                    : ""
                }
                onKeyPress={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                  }
                }}
              />
          </div>
          <div className="mt-[10px] lg:mt-[10px]" />
          <div className={`${innerContainerClass} relative z-[60]`}>
            <SearchLocation
              register={register}
              icon="right"
              name="city"
              defaultValue={formData.city}
              placeholder={globalVariables.city}
              label={globalVariables.city}
              types={["(cities)"]}
              onSelectLocation={(location) => {
                handleFormData("city", location);
              }}
              errorMsg={
                !formData.city && checkValidation
                  ? globalVariables.inputNotMeet
                  : ""
              }
              onKeyPress={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                }
              }}
              searchBy={formData.state}
            />
            <div className="mr-5" />
            <TextInput
              register={register}
              name="zip"
              placeholder={globalVariables.zip}
              label={globalVariables.zip}
              errorMsg={errors.zip?.message}
              onKeyPress={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                }
              }}
            />
          </div>
          <div className="mt-[10px] lg:mt-[10px]" />
          <div className={innerContainerClass}>
            <div className={"lg:block"}>
              <CustomDatePicker
                label={globalVariables.trialDate}
                onSetDate={(value) => {
                  setValue("trialDate", value, { shouldValidate: true }); 
                  handleFormData("trialDate", value);
                }}
                errorMsg={
                  errors.trialDate?.message && !formData.trialDate 
                  ? globalVariables.pleaseSelectDate 
                  : errors.trialDate?.message
                }
                defaultDate={formData.trialDate}
                placeholder={globalVariables.trialDate}
                {...register("trialDate")}
              />
            </div>
            <div className="mr-5" />
            <div className={"w-full lg:flex lg:w-[385px]"}>
              <CustomDatePicker
                width="w-full lg:w-[180px]"
                errorMsg={
                  errors.openDate?.message && !formData.openDate
                  ? globalVariables.pleaseSelectDate 
                  : errors.openDate?.message
                }
                inputWidth="w-[180px]"
                label={globalVariables.openDate}
                onSetDate={(value) => {
                  setValue("openDate", value, { shouldValidate: true }); 
                  handleFormData("openDate", value);
                }}
                defaultDate={formData.openDate}
                placeholder={globalVariables.openDate}
                {...register("openDate")}
              />
              <div className="mt-[10px] lg:mt-0 mr-0 lg:mr-5" />
              <CustomDatePicker
                width="w-full lg:w-[180px]"
                errorMsg={
                  errors.closeDate?.message && !formData.closeDate
                  ? globalVariables.pleaseSelectDate
                  : errors.closeDate?.message
                }
                inputWidth="w-[180px]"
                label={globalVariables.closeDate}
                onSetDate={(value) => {
                  setValue("closeDate", value, { shouldValidate: true });
                  handleFormData("closeDate", value);
                }}
                defaultDate={formData.closeDate}
                placeholder={globalVariables.closeDate}
                {...register("closeDate")}
              />
            </div>
          </div>
          <div className="mt-[10px] lg:mt-[10px]" />
          <div className={innerContainerClass}>
            <div className={"lg:block"}>
              <CustomDatePicker
                label="Cutoff Date *"
                errorMsg={checkValidation && !formData.cutoffDate ? globalVariables.pleaseSelectDate : ""}
                onSetDate={(value) => {
                  setValue("cutoffDate", value, { shouldValidate: true });
                  handleFormData("cutoffDate", value);
                }}
                defaultDate={formData.cutoffDate}
                placeholder="Cutoff Date"
                {...register("cutoffDate")}
              />
              <p className="text-xs text-gray-500 mt-1">
                After this date, members can no longer edit or cancel entries.
              </p>
            </div>
          </div>
          <div className="mt-[10px] lg:mt-[10px]" />
          <div className={innerContainerClass}>
            <AutoCompleteInput
              options={TIME_ZONES}
              placeholder={globalVariables.timeZone}
              errorMsg={errors.timeZone?.message}
              label={globalVariables.timeZone}
              onSelect={(timeZone) => {
                setValue("timeZone", timeZone, { shouldValidate: true }); 
                handleFormData("timeZone", timeZone);
              }}
              defaultOption={formData.timeZone}
            />
            <div className="mt-[10px] lg:mt-0 mr-0 lg:mr-5" />
            <TextInput
              register={register}
              name="maxEntries"
              onKeyPress={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                }
              }}
              placeholder={globalVariables.maxNumberEntries}
              type="number"
              label={globalVariables.maxNumberEntries}
            />
          </div>
          <div className="mt-[10px] lg:mt-[10px]" />
          <div className={innerContainerClass}>
            <FileUpload
              icon={avatar}
              // checkValidation={checkValidation}
              onSetImage={(img) => handleFormData("image", img)}
              defaultImage={formData.image}
              isFileUpload
              label={globalVariables.trialPremiums}
              onRemoveFile={() => {
                setFormData({
                  ...formData,
                  fileName: "",
                  fileSize: "",
                  image: "",
                });
              }}
              setFileLoading={setFileLoading}
              onSetFileData={(fileName, fileSize) => {
                setFormData({ ...formData, fileName, fileSize });
              }}
              defaultFileName={formData.fileName}
              defaultFileSize={formData.fileSize}
            />
            <div className="lg:ml-5 mt-[7px]">
              <TextInput
                register={register}
                name="numberOfJudges"
                onKeyPress={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                  }
                }}
                placeholder={globalVariables.numberOfJudges}
                type="number"
                label={globalVariables.numberOfJudges}
                onChange={(e) => {
                  setNumberJudges(parseInt(e.target?.value));
                }}
              />
              <div className="mt-[10px] lg:mt-[10px] mr-0 lg:mr-5" />
              <MultiSelectChipsWithSearchDropdown
                label={globalVariables.addJudgesNames}
                placeholder={globalVariables.addJudgesNames}
                maxSelectionAllowed={numberJudges}
                onChange={onAddJudges}
                onShowError={(e) => setJudgeError(e)}
                errorMsg={
                  judgeError && `Please choose the Judge(s) for this trial`
                }
                options={judgesDropdownData.map((e) => ({
                  value: e?.name,
                  label: e?.name,
                  authId: e?.authId,
                }))}
                defaultSelectedOptions={formData.addedJudges?.map(judge => judge.label)}
                useDefault
              />
            </div>
          </div>
          <div className="mt-[10px] lg:mt-[10px]" />
          <div className={innerContainerClass}>
            <TextInput
              textTransform="none"
              register={register}
              name="description"
              placeholder={globalVariables.description}
              label={globalVariables.description}
              errorMsg={errors.description?.message}
              onKeyPress={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                }
              }}
            />
            <div className="mt-[10px] lg:mt-[10px] mr-0 lg:mr-5" />
            <TextInput
              textTransform="none"
              register={register}
              name="notes"
              placeholder={globalVariables.notesShowHere}
              label={globalVariables.notes}
              errorMsg={errors.notes?.message}
              onKeyPress={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                }
              }}
            />
          </div>
          <div className={innerContainerClass}>
            <div>
              <div className="mr-5 mt-[20px]" />
            </div>
          </div>
          <div className="mb-6" />
          <div className="hidden lg:flex justify-end">
            <OutlineButton
              width={"w-32"}
              onClick={tapPrevious}
              label={globalVariables.previous}
            />
            <div className="ml-3" />
            <Button
              onClick={onCheckValidation}
              label={globalVariables.next}
              width="w-32"
              disabled={fileLoading || uploadImageLoading}
            />
          </div>
        </div>
        <div className="fixed bottom-0 lg:hidden w-screen p-[10px] flex row item-center justify-end bg-white border-t-2">
          <OutlineButton
            width={"w-32"}
            onClick={tapPrevious}
            label={globalVariables.previous}
          />
          <div className="ml-3" />
          <Button
            onClick={onCheckValidation}
            label={globalVariables.next}
            width="w-32"
            // onlyDisabled={true}
            disabled={fileLoading || uploadImageLoading}
          />
        </div>
      </form>
    </div>
  );
};
